export type ContentType =
  | 'video/webm'
  | 'video/mp4'
  | 'video/x-matroska'
  | 'audio/mpeg'
  | 'audio/wav'
  | 'audio/mp4';

type Profile = {
  /**
   * Extension used in downloaded files, e.g., can be used to achieve a fake mp4
   */
  extension: string;
  /**
   * What the extension should really be according to specs
   */
  trueExtension: string;
  /**
   * Format name for tools such as ffmpeg
   */
  format: string;
  /**
   * Codec to be used when screenapp/frontend creates a file with video
   */
  videoCodec: string;
  /**
   * Codec to be used when screenapp/frontend creates a file with audio
   */
  audioCodec: string;
  /**
   * Video encoder to be used with ffmpeg
   */
  videoCodecFfmpeg: string;
  /**
   * Audio encoder to be used with ffmpeg
   */
  audioCodecFfmpeg: string;
  /**
   * MIME. Check relevant RFCs etc. when defining this
   */
  mime: ContentType;
};

export type ProfileType = 'webm' | 'mp4' | 'mkv' | 'mp3' | 'wav' | 'mp4a';

/**
 * This file answers the question "what do you mean mp4?".
 *
 * We need to have a convention for combinations of different formats
 * (containers + codecs) since there are a zillion different combinations of
 * them and we cannot support everything generally without increasing the abuse
 * potential.
 *
 * This is a map of format name used by convention vs. the actual codecs etc.
 * used. If some parameter (e.g., frame rate, video size), wouldn't practically
 * matter or allowed to be dynamic as far as screenapp is concerned, it doesn't
 * need to be specified here.
 *
 * This map only applies to files output by ScreenApp tools (i.e., transcoding,
 * recording, ...) as far as the codecs are concerned; we cannot control files
 * created by importing via the UI and CLI because they can be anything. But
 * they still have to be any of the allowed formats here and ffmpeg must
 * validate them. Always run ffprobe to identify input file codecs.
 */
const profiles: { [humanReadableName in ProfileType]: Profile } = {
  webm: {
    // https://www.webmproject.org/docs/container/
    extension: '.webm',
    trueExtension: '.webm',
    format: 'webm',
    videoCodec: 'vp8',
    audioCodec: 'opus',
    videoCodecFfmpeg: 'libvpx',
    audioCodecFfmpeg: 'libopus',
    mime: 'video/webm',
  },
  mkv: {
    // https://www.matroska.org/what_is_matroska.html
    extension: '.mkv',
    trueExtension: '.mkv',
    format: 'matroska',
    videoCodec: 'h264',
    audioCodec: 'opus',
    videoCodecFfmpeg: 'libx264',
    audioCodecFfmpeg: 'libopus',
    mime: 'video/x-matroska',
  },
  mp4: {
    extension: '.mp4',
    trueExtension: '.mp4',
    format: 'mp4',
    // Using avc3 for recording to support resolution changes during capture.
    // Backend will convert to avc1 for better playback compatibility.
    videoCodec: 'avc3.64003E',
    audioCodec: 'mp4a.40.2', // mp4a is the codec for aac, but Safari only recognizes mp4a
    videoCodecFfmpeg: 'libx264',
    audioCodecFfmpeg: 'aac',
    mime: 'video/mp4',
  },
  mp4a: {
    extension: '.mp4a',
    trueExtension: '.mp4a',
    format: 'mp4a',
    videoCodec: '',
    audioCodec: 'mp4a.40.2', // mp4a is the codec for aac, but Safari only recognizes mp4a
    videoCodecFfmpeg: '',
    audioCodecFfmpeg: 'aac',
    mime: 'audio/mp4',
  },
  mp3: {
    extension: '.mp3',
    trueExtension: '.mp3',
    format: 'mp3',
    videoCodec: '',
    audioCodec: 'mp3',
    videoCodecFfmpeg: '',
    audioCodecFfmpeg: 'libmp3lame',
    mime: 'audio/mpeg',
  },
  wav: {
    extension: '.wav',
    trueExtension: '.wav',
    format: 'wav',
    videoCodec: '',
    audioCodec: 'pcm_s16le',
    videoCodecFfmpeg: '',
    audioCodecFfmpeg: 'pcm_s16le',
    mime: 'audio/wav',
  },
  // If more formats are needed, just add 'betterMkv': {...} and so on.
};

export default profiles;

/**
 * Standard audio bitrate for high-quality recordings.
 */
export const HIGH_QUALITY_AUDIO_BITRATE = 128000;

/**
 * Low audio bitrate for features like dictation, where speed is prioritized over quality.
 */
export const LOW_QUALITY_AUDIO_BITRATE = 16000;

export function getExtension(profileId: ProfileType): string | undefined {
  return profiles[profileId]?.extension || undefined;
}

export function getMime(profileId: ProfileType): string | undefined {
  return profiles[profileId]?.mime || undefined;
}

interface AudioVideo {
  audio: boolean;
  video: boolean;
}

/**
 * Returns strings like "video/webm;codecs=h264,opus"
 * @param profileId
 * @param param1.video
 * @param param1.audio
 * @returns
 */
export function getMimeWithCodecs(
  profileId: ProfileType,
  { audio, video }: AudioVideo,
) {
  const videoCodec = video ? profiles[profileId]?.videoCodec : '';
  const audioCodec = audio ? profiles[profileId]?.audioCodec : '';
  return `${profiles[profileId]?.mime};codecs=${[videoCodec, audioCodec]
    .filter(Boolean)
    .join(',')}`;
}

export function getFormat(profileId: ProfileType): string | undefined {
  return profiles[profileId]?.format || undefined;
}

export function guessProfileFromMime(mime: ContentType) {
  const profileKey = Object.keys(profiles).find(
    (key): key is ProfileType => profiles[key as ProfileType].mime === mime,
  );
  if (!profileKey) {
    throw new Error(`No profile found for mime type ${mime}`);
  }
  return profileKey;
}

// Test if the browser supports profiles and returns the most suitable one
// Priority is mp4 > mkv > webm
export function getMostSuitableContentType(options?: AudioVideo): ContentType {
  const { audio = true, video = true } = options || {};
  const priority: ProfileType[] = ['mp4', 'mkv', 'webm'];
  const selectedProfile = priority.find((profile) => {
    return MediaRecorder.isTypeSupported(
      getMimeWithCodecs(profile, { audio, video }),
    );
  });
  if (!selectedProfile) {
    return profiles.webm.mime;
  }
  return profiles[selectedProfile].mime;
}
